/*!
 * cueNames.js — the words of the cue language
 * © 2026 Rob Canning — GPLv3
 *
 * The one list of cue names. The dispatcher's scan of a score, the notice
 * that offers to move DSL out of ids (projectLoader.js) and the server route
 * that moves it (server/routes/svgEdit.js), the server's check of a new
 * page's name, the DSL inspector's picker and the live console's completion
 * all read it here. No imports: the server and the node tests load it too.
 * test/cueNames.test.js holds it to the grammar and to the dispatcher: a
 * cue added to one and not here fails it.
 */

// [name, the AST type the parser makes of it, where it is offered]
// A null type is a directive: a preprocessor reads it before the parser
// (drag, propagate, reuse/use, rehearsal marks, notes), so it is no cue.
// Offered: c = the live console's completion, e = the score's DSL
// inspector picker. Both list them in this order.
const WORDS = [
  ["synth", "cueSynth", "ce"],
  ["audio", "cueAudio", "ce"],
  ["audioPool", "cueAudioPool", "ce"],
  ["audioImpulse", "cueAudioImpulse", "ce"],
  ["shape", "cueShape", "c"],
  ["viz", "cueViz", "c"],
  ["hush", "cueHush", "c"],
  ["still", "cueStill", "c"],
  ["text", "cueText", "ce"],
  ["rotate", "cueRotate", "ce"],
  ["scale", "cueScale", "ce"],
  ["scaleXY", "cueScale", "c"],
  ["trans", "cueTrans", "ce"],
  ["o2p", "cueO2P", "ce"],
  ["color", "cueColor", "ce"],
  ["colour", "cueColor", ""],
  ["fade", "cueFade", "ce"],
  ["draw", "cueDraw", "ce"],
  ["zoom", "cueZoom", "ce"],
  ["speed", "cueSpeed", "ce"],
  ["pause", "cuePause", "ce"],
  ["stop", "cueStop", "ce"],
  ["play", "cuePlay", "c"],
  ["rewind", "cueRewind", "c"],
  ["seek", "cueSeek", "c"],
  ["ui", "cueUi", "ce"],
  ["osc", "cueOsc", "ce"],
  ["midi", "cueMidi", "ce"],
  ["metro", "cueMetronome", "ce"],
  ["metronome", "cueMetronome", ""],
  ["nav", "cueNav", "ce"],
  ["page", "cuePage", "ce"],
  ["video", "cueVideo", "ce"],
  ["image", "cueImage", "ce"],
  ["tempo", "cueTempo", "c"],
  ["capture", "cueCapture", "c"],
  ["resources", "cueResources", "c"],
  ["screen", "cueScreen", "c"],
  ["scatter", "cueScatter", "c"],
  ["use", null, "ce"],
  ["vu", "cueVu", "c"],
  ["level", "cueLevel", "c"],
  ["xfade", "cueXfade", "c"],
  ["hold", "cueHold", "c"],
  ["gate", "cueGate", "c"],
  ["seq", "cueSeq", "c"],
  ["bang", "cueBang", "c"],
  ["say", "cueSay", "c"],
  ["fx", "cueFx", "c"],
  ["speakers", "cueSpeakers", "c"],
  ["group", "cueGroup", "c"],
  ["style", "cueStyle", "c"],
  ["solo", "cueSolo", "c"],
  ["grid", "cueGrid", "c"],
  ["adc", "cueAdc", "ce"],
  ["cam", "cueCam", "c"],
  ["scene", "cueScene", "c"],
  ["scenes", "cueScenes", "c"],
  ["wait", "cueWait", "c"],
  ["console", "cueConsole", "c"],
  ["strobe", "cueStrobe", "c"],
  ["palette", "cuePalette", "c"],
  // on a score's element only
  ["ext", "cueExt", "e"],
  ["pin", "cuePin", "e"],
  ["stopwatch", "cueStopwatch", "e"],
  ["button", "cueButton", "e"],
  ["oscOut", "cueOscOut", "e"],
  ["display", "cueDisplay", "e"],
  ["url", "cueUrl", "e"],
  ["oscCtrl", "oscCtrl", "e"],
  ["controlXY", "cueControlXY", ""],
  ["notation", "cueNotation", ""],
  ["random", "cueRandom", ""],
  ["station", "cueStation", ""],
  ["leg", "cueLeg", ""],
  ["drag", null, "e"],
  ["rehearsal", null, "e"],
  ["notes", null, "e"],
  ["reuse", null, "e"],
  ["propagate", null, "e"],
];

/** Every cue, by the name written in the DSL. */
export const CUE_NAMES = WORDS.filter(w => w[1]).map(w => w[0]);

/** name -> the AST type the parser makes of it (scaleXY -> cueScale). */
export const CUE_TYPES = Object.fromEntries(WORDS.filter(w => w[1]).map(w => [w[0], w[1]]));

/** The words the preprocessors read before the parser. */
export const DIRECTIVE_NAMES = WORDS.filter(w => !w[1]).map(w => w[0]);

/** The cues and directives offered in the console ("c") or the inspector ("e"), in order. */
export const offeredIn = where => WORDS.filter(w => w[2].includes(where)).map(w => w[0]);

/**
 * DSL that begins with a cue (or drag(), whose element can carry cues after
 * it): what a score's element is scanned for, and what an id offers to move.
 */
export const CUE_START_RE = new RegExp(`^(?:cue:)?(?:${[...CUE_NAMES, "drag"].join("|")})\\s*\\(`, "i");

/** The pattern calls: Pseq([...], 2) ... (the parser's PatternName token). */
export const PATTERN_NAMES = ["Pseq", "Prand", "Pxrand", "Pshuf", "Pchoose", "Pwhite", "Pexprand",
  // chance, as SuperCollider has it (patterns.js): weighted, walks, distributions, Euclidean rhythms
  "Pwrand", "Pbrown", "Pgauss", "Plprand", "Phprand", "Pmeanrand", "Pbeta", "Pcauchy", "Ppoisson", "Pbjorklund", "Peuclid"];

/**
 * The same, as the docs write them: pseq(...), pwhite(...). Only a call:
 * before "(" -- uid:pseq stays a name.
 */
export const PATTERN_WORDS = PATTERN_NAMES.map(n => n.toLowerCase());

/** How a pattern is written back, for a label: Pseq is pseq. */
export function patternWord(name) {
  return String(name ?? "").toLowerCase();
}

/** The pattern a word calls: pseq and Pseq are Pseq. */
export function patternName(word) {
  const w = String(word ?? "");
  return /^p/.test(w) ? "P" + w.slice(1) : w;
}

/**
 * Words the lexer always takes as keywords, so they are no plain name:
 * page(fade) does not parse. The others are keywords only before "(", or
 * plain identifiers behind a gate. Read by the server's check of a page name.
 */
export const KEYWORDS = [
  "cue", "fade", "page", "stopwatch", "video", "text", "pause", "stop", "pin",
  "nav", "audio", "audioPool", "audioImpulse", "synth", "button", "rotate",
  "trans", "scale", "scaleXY", "osc", "midi", "oscCtrl", "controlXY", "o2p",
  "ui", "ext", "notation", "after", "true", "false",
];
